import { useEffect, useRef } from 'react';
import { IProof } from 'src/ts/interfaces';
import {
  buildAutoRuleUpdates,
  FormValues,
  UNVERIFIED_OFFER_LETTER_REASON
} from '../helpers/proofHelpers';
import { OlvLockState } from './useOlvLockState';

interface UseBlockedEditedAutoRuleProps {
  olvLockState: OlvLockState;
  offerLetterProofs: IProof[];
  setLocalFormData: (
    updater: (prev: Record<string, FormValues>) => Record<string, FormValues>
  ) => void;
}

/**
 *
 * The hook is extracted from `ModernizedFraudAnalystReview` (where the effect
 * originally lived inline) so its behavior can be tested in isolation via
 * `renderHook`, per the parent component's convention of avoiding deep
 * integration tests (see `ModernizedFraudAnalystReview.test.tsx` header).
 */
export function useBlockedEditedAutoRule({
  olvLockState,
  offerLetterProofs,
  setLocalFormData
}: UseBlockedEditedAutoRuleProps): void {
  // ITL-2894: previous lock state, so the reset effect can detect a transition
  // OUT of 'blocked_edited' (the no-inverse gap close).
  const prevOlvLockState = useRef<OlvLockState>(olvLockState);

  useEffect(() => {
    const prevLock = prevOlvLockState.current;
    prevOlvLockState.current = olvLockState;

    if (prevLock !== 'blocked_edited' || olvLockState === 'blocked_edited') return;

    setLocalFormData((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        const entry = next[id];
        if (
          entry &&
          entry.result === 'UNDETERMINED' &&
          entry.olvReasons?.length === 1 &&
          entry.olvReasons[0] === UNVERIFIED_OFFER_LETTER_REASON
        ) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [olvLockState, setLocalFormData]);

  useEffect(() => {
    if (olvLockState !== 'blocked_edited' || offerLetterProofs.length === 0) return;

    // Pure update (no ref mutation in the updater): reset-on-unlock identifies the
    // auto-ruled drafts by signature, so no per-id tracking is needed here.
    setLocalFormData((prev) => buildAutoRuleUpdates(offerLetterProofs, prev));
  }, [olvLockState, offerLetterProofs, setLocalFormData]);
}
